import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck, Search, Eye, Plus, IndianRupee, CreditCard, FileText,
  RefreshCw, TrendingDown, Wallet, Phone, Mail, Building2, CheckCircle, AlertTriangle
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VendorSuggestInput } from '@/components/hq/VendorSuggestInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ledgersAPI, vendorsAPI } from '@/services/api';
import VendorPaymentDuePanel from '@/components/hq/VendorPaymentDuePanel';

const paymentModes = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'rtgs', label: 'RTGS' },
  { value: 'neft', label: 'NEFT' }
];

const VendorLedgerPage = () => {
  const navigate = useNavigate();
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dueRefreshKey, setDueRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('balances');
  const [pickedVendor, setPickedVendor] = useState(null);
  const [balanceView, setBalanceView] = useState('all');
  const [due, setDue] = useState({});
  const [dueByVendor, setDueByVendor] = useState({});
  const [reconciling, setReconciling] = useState(false);

  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    vendor_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'bank_transfer',
    amount: '',
    paid_from_bank: '',
    paid_from_account: '',
    cheque_number: '',
    cheque_date: '',
    transaction_reference: '',
    notes: '',
    selected_invoices: []
  });
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Initialize ledger dialog
  const [initDialogOpen, setInitDialogOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [initData, setInitData] = useState({
    vendor_id: '',
    opening_balance: 0
  });
  const [initProcessing, setInitProcessing] = useState(false);

  const { toast } = useToast();

  const fetchLedgers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ledgersAPI.listVendorLedgers({ page: 1, page_size: 100 });
      setLedgers(response.data?.items || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch ledgers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchDue = useCallback(async () => {
    try {
      const res = await ledgersAPI.vendorPaymentReminders({ days_ahead: 7 });
      setDue(res.data?.summary || {});
      const map = {};
      (res.data?.vendors || []).forEach(v => { map[v.vendor_id] = v; });
      setDueByVendor(map);
    } catch (error) {
      setDue({});
    }
  }, []);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const res = await ledgersAPI.reconcileVendorLedgers();
      toast({ title: 'Balances recalculated', description: `${res.data.vendors} vendor(s) checked, ${res.data.backfilled_entries} missing entries added` });
      fetchLedgers();
      setDueRefreshKey(k => k + 1);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to recalculate', variant: 'destructive' });
    } finally {
      setReconciling(false);
    }
  };

  const fetchVendors = useCallback(async () => {
    try {
      const response = await vendorsAPI.list({ is_active: true });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  }, []);

  useEffect(() => {
    fetchLedgers();
    fetchVendors();
  }, [fetchLedgers, fetchVendors]);

  useEffect(() => { fetchDue(); }, [fetchDue, dueRefreshKey]);

  // Navigate to full page statement
  const handleViewStatement = (ledger) => {
    navigate(`/hq/ledgers/vendor/${ledger.vendor_id}/statement`);
  };

  const handleOpenPayment = (ledger = null) => {
    setPaymentData({
      vendor_id: ledger?.vendor_id || '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'bank_transfer',
      amount: '',
      paid_from_bank: '',
      paid_from_account: '',
      cheque_number: '',
      cheque_date: '',
      transaction_reference: '',
      notes: '',
      selected_invoices: []
    });
    setPendingInvoices([]);
    if (ledger?.vendor_id) {
      fetchPendingInvoices(ledger.vendor_id);
    }
    setPaymentDialogOpen(true);
  };

  const fetchPendingInvoices = async (vendorId) => {
    if (!vendorId) return;
    setLoadingInvoices(true);
    try {
      const response = await ledgersAPI.getVendorLedgerEntries(vendorId, { 
        page_size: 100,
        entry_type: 'credit'  // GRNs are credit entries (we owe vendor)
      });
      const entries = response.data?.items || [];
      // Filter only unpaid/partially paid invoices (credit entries without matching debit)
      const credits = entries.filter(e => e.entry_type === 'credit' && e.credit_amount > 0);
      setPendingInvoices(credits);
    } catch (error) {
      console.error('Failed to fetch pending invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleVendorChange = (vendorId) => {
    setPaymentData(d => ({ ...d, vendor_id: vendorId, selected_invoices: [], amount: '' }));
    fetchPendingInvoices(vendorId);
  };

  const handleInvoiceSelect = (invoice, isSelected) => {
    let newSelected;
    if (isSelected) {
      newSelected = [...paymentData.selected_invoices, invoice];
    } else {
      newSelected = paymentData.selected_invoices.filter(i => i.id !== invoice.id);
    }
    const totalAmount = newSelected.reduce((sum, inv) => sum + (inv.credit_amount || 0), 0);
    setPaymentData(d => ({ 
      ...d, 
      selected_invoices: newSelected,
      amount: totalAmount.toFixed(2)
    }));
  };

  const handleCreatePayment = async () => {
    if (!paymentData.vendor_id || !paymentData.amount) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    setPaymentProcessing(true);
    try {
      await ledgersAPI.createVendorPayment({
        ...paymentData,
        amount: parseFloat(paymentData.amount),
        selected_invoice_ids: paymentData.selected_invoices.map(i => i.id),
        selected_invoice_refs: paymentData.selected_invoices.map(i => i.reference_number).join(', ')
      });
      toast({ title: 'Success', description: 'Payment recorded successfully' });
      setPaymentDialogOpen(false);
      fetchLedgers();
      setDueRefreshKey(k => k + 1);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to record payment',
        variant: 'destructive'
      });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleInitializeLedger = async () => {
    if (!initData.vendor_id) {
      toast({ title: 'Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }

    setInitProcessing(true);
    try {
      await ledgersAPI.initializeVendorLedger(initData.vendor_id, {
        opening_balance: parseFloat(initData.opening_balance) || 0
      });
      toast({ title: 'Success', description: 'Ledger initialized successfully' });
      setInitDialogOpen(false);
      fetchLedgers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to initialize ledger',
        variant: 'destructive'
      });
    } finally {
      setInitProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    // Grand totals / balances rounded to nearest rupee (no paise)
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Math.round(Number(amount) || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN');
  };

  const totalPayable = ledgers.reduce((sum, l) => sum + Math.max(0, l.current_balance || 0), 0);
  const totalPurchases = ledgers.reduce((sum, l) => sum + (l.total_purchases || 0), 0);
  const totalPaid = ledgers.reduce((sum, l) => sum + (l.total_paid || 0), 0);
  const withBalance = ledgers.filter(l => (l.current_balance || 0) > 0);
  const settled = ledgers.filter(l => !((l.current_balance || 0) > 0));
  const suggestVendors = ledgers.map(l => ({ id: l.vendor_id, name: l.vendor_name, code: l.vendor_code }));
  const visibleLedgers = ledgers
    .filter(l => !pickedVendor || l.vendor_id === pickedVendor.id)
    .filter(l => balanceView === 'all' || (balanceView === 'balance' ? (l.current_balance || 0) > 0 : !((l.current_balance || 0) > 0)))
    .sort((a, b) => (b.current_balance || 0) - (a.current_balance || 0));

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="vendor-ledger-page">
      <Toaster />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="w-6 h-6 text-purple-600" />
            Vendor Ledger
          </h1>
          <p className="text-gray-500 mt-1">What you owe each vendor — purchases (GRN) minus payments, with overdue bills by credit days</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleReconcile} disabled={reconciling} className="border-gray-300" data-testid="reconcile-btn" title="Recalculate every vendor balance from all GRNs, payments and voids">
            <RefreshCw className={`w-4 h-4 mr-2 ${reconciling ? 'animate-spin' : ''}`} /> {reconciling ? 'Recalculating…' : 'Recalculate Balances'}
          </Button>
          <Button onClick={() => setInitDialogOpen(true)} variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50" data-testid="init-ledger-btn">
            <Plus className="w-4 h-4 mr-2" /> Initialize Ledger
          </Button>
          <Button onClick={() => handleOpenPayment()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="record-payment-btn">
            <CreditCard className="w-4 h-4 mr-2" /> Make Payment
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gray-900 border-gray-900 text-white">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wide text-gray-300">Total payable now</p>
            <p className="text-3xl font-bold mt-1" data-testid="summary-total-payable">{formatCurrency(totalPayable)}</p>
            <p className="text-xs text-gray-400 mt-1">{withBalance.length} vendor(s) with balance · {settled.length} settled</p>
          </CardContent>
        </Card>
        <Card className={`${(due.overdue_amount || 0) > 0 ? 'bg-red-600 border-red-700 text-white' : 'bg-white border-gray-200'}`}>
          <CardContent className="p-5">
            <p className={`text-xs uppercase tracking-wide ${(due.overdue_amount || 0) > 0 ? 'text-red-100' : 'text-gray-400'}`}>Overdue</p>
            <p className="text-3xl font-bold mt-1" data-testid="summary-overdue">{formatCurrency(due.overdue_amount)}</p>
            <p className={`text-xs mt-1 ${(due.overdue_amount || 0) > 0 ? 'text-red-100' : 'text-gray-500'}`}>{due.overdue_count || 0} bill(s) past credit days</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wide text-amber-600">Due in next 7 days</p>
            <p className="text-3xl font-bold text-amber-700 mt-1" data-testid="summary-due-soon">{formatCurrency(due.due_soon_amount)}</p>
            <p className="text-xs text-amber-600 mt-1">{due.due_soon_count || 0} bill(s)</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Purchases vs paid</p>
            <p className="text-xl font-bold text-gray-800 mt-1">{formatCurrency(totalPurchases)}</p>
            <p className="text-xs text-emerald-600 mt-1">Paid {formatCurrency(totalPaid)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-gray-200 mb-4 h-auto p-1 flex-wrap">
          <TabsTrigger value="balances" className="data-[state=active]:bg-gray-900 data-[state=active]:text-white px-4" data-testid="tab-balances">
            Vendor Balances
          </TabsTrigger>
          <TabsTrigger value="overdue" className="data-[state=active]:bg-red-600 data-[state=active]:text-white px-4" data-testid="tab-overdue">
            Overdue <span className="ml-1.5 text-xs rounded-full bg-red-100 text-red-700 px-1.5">{due.overdue_count || 0}</span>
          </TabsTrigger>
          <TabsTrigger value="due_soon" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white px-4" data-testid="tab-due-soon">
            Due Soon <span className="ml-1.5 text-xs rounded-full bg-amber-100 text-amber-700 px-1.5">{due.due_soon_count || 0}</span>
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-gray-700 data-[state=active]:text-white px-4" data-testid="tab-upcoming">
            Upcoming
          </TabsTrigger>
        </TabsList>

        <TabsContent value="balances">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4 border-b border-gray-100 flex flex-col md:flex-row gap-3 md:items-center">
              <div className="w-full md:w-96">
                <VendorSuggestInput
                  vendors={suggestVendors}
                  value={pickedVendor}
                  onSelect={setPickedVendor}
                  placeholder="Find vendor by name or code..."
                  testId="ledger-vendor-search"
                />
              </div>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-sm">
                {[['all', 'All'], ['balance', 'With balance'], ['settled', 'Settled']].map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setBalanceView(k)}
                    className={`px-3 py-1 rounded-md ${balanceView === k ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500'}`}
                    data-testid={`balance-view-${k}`}>
                    {label}
                  </button>
                ))}
              </div>
              <span className="text-sm text-gray-500 md:ml-auto">{visibleLedgers.length} vendor(s)</span>
            </CardContent>
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading ledgers...</div>
              ) : visibleLedgers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No vendor ledgers found</p>
                  <p className="text-sm mt-2">Initialize a ledger for vendors to track payables</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-gray-700">Vendor</TableHead>
                      <TableHead className="font-semibold text-gray-700 text-right">Purchases</TableHead>
                      <TableHead className="font-semibold text-gray-700 text-right">Paid</TableHead>
                      <TableHead className="font-semibold text-gray-700 text-right">Returns</TableHead>
                      <TableHead className="font-semibold text-gray-700 text-right">Current Balance</TableHead>
                      <TableHead className="font-semibold text-gray-700 text-right">Overdue</TableHead>
                      <TableHead className="font-semibold text-gray-700">Last Payment</TableHead>
                      <TableHead className="font-semibold text-gray-700 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleLedgers.map(ledger => {
                      const bal = Number(ledger.current_balance || 0);
                      const od = dueByVendor[ledger.vendor_id];
                      return (
                        <TableRow key={ledger.id} className="hover:bg-gray-50" data-testid={`ledger-row-${ledger.id}`}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{ledger.vendor_name}</p>
                            <p className="text-xs text-gray-500">{ledger.vendor_code}{ledger.contact_mobile ? ` · ${ledger.contact_mobile}` : ''}</p>
                          </TableCell>
                          <TableCell className="text-right text-gray-700">{formatCurrency(ledger.total_purchases)}</TableCell>
                          <TableCell className="text-right text-emerald-600">{formatCurrency(ledger.total_paid)}</TableCell>
                          <TableCell className="text-right text-blue-600">{formatCurrency(ledger.total_returns)}</TableCell>
                          <TableCell className="text-right">
                            <span className={`text-lg font-bold ${bal > 0 ? 'text-red-600' : bal < 0 ? 'text-blue-600' : 'text-gray-500'}`} data-testid={`balance-${ledger.vendor_code}`}>
                              {formatCurrency(Math.abs(bal))}
                            </span>
                            <span className={`block text-[11px] font-semibold uppercase tracking-wide ${bal > 0 ? 'text-red-500' : bal < 0 ? 'text-blue-500' : 'text-emerald-600'}`}>
                              {bal > 0 ? 'Payable' : bal < 0 ? 'Advance' : 'Settled'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {od?.overdue_amount ? (
                              <span className="inline-flex items-center gap-1 text-red-600 font-semibold" data-testid={`overdue-${ledger.vendor_code}`}>
                                <AlertTriangle className="w-3.5 h-3.5" /> {formatCurrency(od.overdue_amount)}
                                <span className="text-xs font-normal">({od.max_days_overdue}d)</span>
                              </span>
                            ) : <span className="text-gray-400">—</span>}
                          </TableCell>
                          <TableCell><span className="text-sm text-gray-500">{formatDate(ledger.last_payment_date)}</span></TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleViewStatement(ledger)} className="text-gray-600 hover:text-purple-600" data-testid={`view-ledger-${ledger.id}`}>
                                <Eye className="w-4 h-4 mr-1" /> Statement
                              </Button>
                              <Button size="sm" onClick={() => handleOpenPayment(ledger)} disabled={bal <= 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`pay-${ledger.vendor_code}`}>
                                <CreditCard className="w-4 h-4 mr-1" /> Pay
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overdue">
          <VendorPaymentDuePanel fixedStatus="overdue" refreshKey={dueRefreshKey} onPay={(bill) => handleOpenPayment({ vendor_id: bill.vendor_id })} />
        </TabsContent>
        <TabsContent value="due_soon">
          <VendorPaymentDuePanel fixedStatus="due_today,due_soon" refreshKey={dueRefreshKey} onPay={(bill) => handleOpenPayment({ vendor_id: bill.vendor_id })} />
        </TabsContent>
        <TabsContent value="upcoming">
          <VendorPaymentDuePanel fixedStatus="upcoming" refreshKey={dueRefreshKey} onPay={(bill) => handleOpenPayment({ vendor_id: bill.vendor_id })} />
        </TabsContent>
      </Tabs>
      {/* Payment Dialog - Fixed wider UI */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-hidden flex flex-col" data-testid="payment-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Make Vendor Payment
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {/* Vendor Selection */}
            <div className="space-y-2">
              <Label className="text-gray-700">Vendor *</Label>
              <Select
                value={paymentData.vendor_id}
                onValueChange={handleVendorChange}
              >
                <SelectTrigger className="bg-white border-gray-300" data-testid="payment-vendor-select">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent className="bg-white z-[100]">
                  {ledgers.map(l => (
                    <SelectItem key={l.vendor_id} value={l.vendor_id}>
                      {l.vendor_name} ({l.vendor_code}) - Balance: {formatCurrency(l.current_balance)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Pending Invoices/GRNs Section - Compact layout */}
            {paymentData.vendor_id && (
              <div className="space-y-2">
                <Label className="text-gray-700">Select Invoices/GRNs to Pay (Optional)</Label>
                <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto">
                  {loadingInvoices ? (
                    <div className="p-3 text-center text-gray-500 text-sm">Loading pending invoices...</div>
                  ) : pendingInvoices.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-sm">No pending invoices found</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {pendingInvoices.map((inv) => {
                        const isSelected = paymentData.selected_invoices.some(s => s.id === inv.id);
                        return (
                          <div 
                            key={inv.id} 
                            className={`p-2 flex items-center gap-2 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-emerald-50' : ''}`}
                            onClick={() => handleInvoiceSelect(inv, !isSelected)}
                          >
                            <input 
                              type="checkbox" 
                              checked={isSelected}
                              onChange={() => {}}
                              className="w-4 h-4 text-emerald-600 rounded border-gray-300 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{inv.reference_number}</p>
                              <p className="text-xs text-gray-500 truncate">{inv.description}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-semibold text-red-600">{formatCurrency(inv.credit_amount)}</p>
                              <p className="text-xs text-gray-400">{formatDate(inv.entry_date)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {paymentData.selected_invoices.length > 0 && (
                  <div className="flex justify-between items-center p-2 bg-emerald-50 rounded-lg">
                    <span className="text-sm text-emerald-700">{paymentData.selected_invoices.length} invoice(s) selected</span>
                    <span className="font-semibold text-emerald-700">{formatCurrency(paymentData.amount)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Payment Date *</Label>
                <Input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData(d => ({ ...d, payment_date: e.target.value }))}
                  className="bg-white border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Amount *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(d => ({ ...d, amount: e.target.value }))}
                  className="bg-white border-gray-300"
                  data-testid="payment-amount-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Payment Mode *</Label>
              <Select
                value={paymentData.payment_mode}
                onValueChange={(v) => setPaymentData(d => ({ ...d, payment_mode: v }))}
              >
                <SelectTrigger className="bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white z-[100]">
                  {paymentModes.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {paymentData.payment_mode === 'cheque' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Cheque Number</Label>
                  <Input
                    value={paymentData.cheque_number}
                    onChange={(e) => setPaymentData(d => ({ ...d, cheque_number: e.target.value }))}
                    className="bg-white border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Cheque Date</Label>
                  <Input
                    type="date"
                    value={paymentData.cheque_date}
                    onChange={(e) => setPaymentData(d => ({ ...d, cheque_date: e.target.value }))}
                    className="bg-white border-gray-300"
                  />
                </div>
              </div>
            )}

            {['bank_transfer', 'upi', 'rtgs', 'neft'].includes(paymentData.payment_mode) && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Paid From Bank</Label>
                    <Input
                      value={paymentData.paid_from_bank}
                      onChange={(e) => setPaymentData(d => ({ ...d, paid_from_bank: e.target.value }))}
                      placeholder="Our bank name"
                      className="bg-white border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Account Number</Label>
                    <Input
                      value={paymentData.paid_from_account}
                      onChange={(e) => setPaymentData(d => ({ ...d, paid_from_account: e.target.value }))}
                      placeholder="Our account"
                      className="bg-white border-gray-300"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Transaction Reference</Label>
                  <Input
                    value={paymentData.transaction_reference}
                    onChange={(e) => setPaymentData(d => ({ ...d, transaction_reference: e.target.value }))}
                    placeholder="UTR / Reference Number"
                    className="bg-white border-gray-300"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label className="text-gray-700">Notes</Label>
              <Textarea
                value={paymentData.notes}
                onChange={(e) => setPaymentData(d => ({ ...d, notes: e.target.value }))}
                placeholder="Payment notes..."
                className="bg-white border-gray-300"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 gap-2 pt-4 border-t border-gray-100">
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} disabled={paymentProcessing}>
              Cancel
            </Button>
            <Button
              onClick={handleCreatePayment}
              disabled={paymentProcessing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="payment-submit-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {paymentProcessing ? 'Processing...' : 'Make Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Initialize Ledger Dialog */}
      <Dialog open={initDialogOpen} onOpenChange={setInitDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Plus className="w-5 h-5 text-purple-600" />
              Initialize Vendor Ledger
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Vendor *</Label>
              <Select
                value={initData.vendor_id}
                onValueChange={(v) => setInitData(d => ({ ...d, vendor_id: v }))}
              >
                <SelectTrigger className="bg-white border-gray-300">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {vendors
                    .filter(v => !ledgers.some(l => l.vendor_id === v.id))
                    .map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} ({v.code})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {vendors.filter(v => !ledgers.some(l => l.vendor_id === v.id)).length === 0 && (
                <p className="text-xs text-amber-600">All vendors already have ledgers initialized.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Opening Balance</Label>
              <Input
                type="number"
                placeholder="0.00"
                value={initData.opening_balance}
                onChange={(e) => setInitData(d => ({ ...d, opening_balance: e.target.value }))}
                className="bg-white border-gray-300"
              />
              <p className="text-xs text-gray-500">Positive = we owe the vendor</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInitDialogOpen(false)} disabled={initProcessing}>
              Cancel
            </Button>
            <Button
              onClick={handleInitializeLedger}
              disabled={initProcessing || !initData.vendor_id}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {initProcessing ? 'Creating...' : 'Initialize Ledger'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorLedgerPage;
